import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import { PasswordInput } from '../components/PasswordInput';
import LegalFooter from '../components/LegalFooter';

const Login: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { from?: { pathname?: string }; resetDone?: boolean } | null;
  const from = state?.from?.pathname || '/';

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await login(username.trim(), password);
      navigate(from, { replace: true });
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Login failed. Check credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg)',
        padding: 24,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 380,
          background: 'var(--surface)',
          border: '2px solid var(--border)',
          boxShadow: '8px 8px 0 rgba(0,0,0,0.6)',
          padding: '40px 36px',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <img
            src={`${process.env.PUBLIC_URL}/icon.png`}
            alt="Dial Sniper"
            style={{ height: 56, marginBottom: 12 }}
          />
          <h1
            style={{
              margin: 0,
              color: 'var(--amber)',
              fontFamily: 'var(--font-display)',
              letterSpacing: '0.06em',
            }}
          >
            Dial Sniper
          </h1>
          <p
            style={{
              margin: '8px 0 0',
              color: 'var(--text-faint)',
              fontFamily: 'var(--font-data)',
              fontSize: 12,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
            }}
          >
            Internal Access
          </p>
        </div>

        {error && (
          <div
            style={{
              marginBottom: 16,
              padding: '10px 12px',
              border: '1px solid var(--category-dead-fg)',
              background: 'var(--category-dead-bg)',
              color: 'var(--category-dead-fg)',
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {!error && state?.resetDone && (
          <div
            style={{
              marginBottom: 16,
              padding: '10px 12px',
              border: '1px solid var(--border)',
              background: 'var(--surface-subtle)',
              color: 'var(--text-soft)',
              fontSize: 13,
            }}
          >
            Password updated. Please sign in with your new password.
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <label style={{ display: 'block', marginBottom: 12 }}>
            <div style={{ marginBottom: 6, color: 'var(--text-soft)', fontSize: 12, textTransform: 'uppercase' }}>
              Username or Email
            </div>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
              style={inputStyle}
            />
          </label>

          <label style={{ display: 'block', marginBottom: 20 }}>
            <div style={{ marginBottom: 6, color: 'var(--text-soft)', fontSize: 12, textTransform: 'uppercase' }}>
              Password
            </div>
            <PasswordInput
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
              required
              style={inputStyle}
            />
          </label>

          <button type="submit" disabled={loading} className="sidebar-new-run-btn" style={{ width: '100%' }}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div
          style={{
            marginTop: 18,
            display: 'flex',
            justifyContent: 'space-between',
            gap: 12,
            fontSize: 12,
          }}
        >
          <Link to="/signup" style={linkStyle}>Create account</Link>
          <Link to="/forgot-password" style={linkStyle}>Forgot password?</Link>
        </div>

        <LegalFooter />
      </div>
    </div>
  );
};

const linkStyle: React.CSSProperties = {
  color: 'var(--text-faint)',
  textDecoration: 'none',
  letterSpacing: '0.04em',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  background: 'var(--surface-subtle)',
  color: 'var(--text)',
  border: '1px solid var(--border)',
  outline: 'none',
};

export default Login;
